import { Component, OnInit, ViewChild } from '@angular/core';
import { TrainingsApiService } from '../../services/api/trainings.api.service';
import { TrainingBlock, TrainingBlockStatus } from 'src/app/classes/TrainingBlock.class';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'trainings',
  templateUrl: './trainings.screen.html',
  styleUrls: ['./trainings.screen.scss'],
})
export class TrainingsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<TrainingBlock>;
  displayedColumns: string[] = [];
  activeColumns: string[] = ['sort', 'ordering', 'url', 'createdAt', 'details'];
  inactiveColumns: string[] = ['url', 'createdAt', 'reactivate'];
  searchTerm: string;
  blocks: TrainingBlock[] = [];
  block: TrainingBlock = new TrainingBlock();
  newBlockModal = false;
  deleteBlockModal = false;
  detailBlockModal = false;
  infoModal = false;
  reactivationModal = false;
  error = false;
  errorMessage = '';
  modalText = '';
  loadingIndicator = false;
  activeTab: string;

  constructor(private readonly trainingsService: TrainingsApiService) {}

  async ngOnInit(): Promise<void> {
    await this.dataList('active');
  }

  async dataList(type = 'active') {
    this.activeTab = type;
    await this.loadBlocks(type);
  }

  async handleBlockAction(action: 'save' | 'delete', block: TrainingBlock): Promise<void> {
    let response;

    if (action === 'save') {
      response = await this.trainingsService.saveBlock(block);
      this.setModalText('response.save_company');
    } else if (action === 'delete') {
      response = await this.trainingsService.deleteBlock(block);
      this.setModalText('response.delete');

      if (!response.code) {
        await this.updateOrderingAfterDelete(block);
      }
    }

    if (!response.code) {
      this.closeModalsAndReload();
    } else {
      this.handleError(response.errors[0].message);
    }
  }

  async reactivateBlock(): Promise<void> {
    this.setModalText('training.confirm_reactivation_text');
    const activeBlocks = await this.trainingsService.getReleasedBlocks();
    const response = await this.trainingsService.blockUndoDelete(
      this.block,
      activeBlocks.length + 1,
    );
    if (!response.code) {
      this.closeModalsAndReload();
    } else {
      this.handleError(response.errors[0].message);
    }
  }

  async sortUpDown(order: number, direction: 'up' | 'down'): Promise<void> {
    this.loadingIndicator = true;

    const currentIndex = this.blocks.findIndex(block => block.ordering === order);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < this.blocks.length) {
      const tempOrdering = this.blocks[currentIndex].ordering;
      this.blocks[currentIndex].ordering = this.blocks[newIndex].ordering;
      this.blocks[newIndex].ordering = tempOrdering;

      this.blocks.sort((a, b) => a.ordering - b.ordering);
      this.dataSource.data = this.blocks;

      await Promise.all(
        [this.blocks[currentIndex], this.blocks[newIndex]].map(async block => {
          block.dirty = true;
          await this.trainingsService.saveChangedBlockOrders(block);
        }),
      );
    }

    this.loadingIndicator = false;
  }

  getSelectedBlock(id: number): void {
    this.block = this.blocks.find(block => block.id === id);
    this.detailBlockModal = true;
  }

  openNewBlockModal(): void {
    this.block = {
      url: '',
      ordering: this.blocks.length + 1,
      status: TrainingBlockStatus.Released,
    };
    this.error = false;
    this.newBlockModal = true;
  }

  openDeleteBlockModal(): void {
    this.deleteBlockModal = true;
    this.detailBlockModal = false;
  }

  openReactivationModal(id: number): void {
    this.block = this.blocks.find(block => block.id === id);
    this.reactivationModal = true;
  }

  filterResults(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  closeModal() {
    this.newBlockModal =
      this.detailBlockModal =
      this.deleteBlockModal =
      this.infoModal =
      this.reactivationModal =
        false;
  }

  private setModalText(val: string): void {
    this.modalText = val;
  }

  private async loadBlocks(type): Promise<void> {
    this.displayedColumns = type === 'inactive' ? this.inactiveColumns : this.activeColumns;
    if (type === 'active') {
      this.blocks = await this.trainingsService.getReleasedBlocks();
    } else {
      this.blocks = await this.trainingsService.getDeletedBlocks();
    }

    this.blocks.sort((a, b) => a.ordering - b.ordering);
    this.dataSource = new MatTableDataSource<TrainingBlock>(this.blocks);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data, filter) => {
      const dataStr = JSON.stringify(data).toLowerCase();
      const filterStr = filter.toLowerCase();
      return dataStr.includes(filterStr);
    };
  }

  private async updateOrderingAfterDelete(deletedBlock: TrainingBlock): Promise<void> {
    const deletedIndex = this.blocks.findIndex(b => b.id === deletedBlock.id);
    const deletedOrdering = this.blocks[deletedIndex].ordering;

    for (let i = deletedIndex + 1; i < this.blocks.length; i++) {
      if (this.blocks[i].ordering > deletedOrdering) {
        this.blocks[i].ordering -= 1;
        await this.trainingsService.saveBlock(this.blocks[i]);
      }
    }
  }

  private handleError(errorMessage: string): void {
    this.error = true;
    this.errorMessage = errorMessage;
  }

  private closeModalsAndReload(): void {
    this.closeModal();
    setTimeout(() => {
      this.infoModal = true;
    }, 800);
    setTimeout(() => {
      this.closeModal();
      window.location.reload();
    }, 2500);
  }
}
